import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { compressImage } from '../utils/imageCompression';
import PhotoLightbox from '../components/PhotoLightbox';
import HelpButton from '../components/HelpButton';

export default function InspirationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inspirations, setInspirations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await api.listInspirations();
      setInspirations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar esta foto de inspiración?')) return;
    try {
      await api.deleteInspiration(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <button className="btn btn-ghost" onClick={() => navigate('/home')}>← Volver</button>
        <HelpButton title="📸 Inspiraciones">
          <p>Este es un muro abierto donde toda la familia puede ver lo que a cada uno le gusta, durante todo el año (no solo para Reyes — también vale para cumpleaños, aniversarios, etc.).</p>
          <p>Si ves algo que te gustaría tener, súbelo aquí con una foto (de la cámara o de tu galería) y un comentario opcional.</p>
          <p>Aquí <strong>sí se ve quién subió cada foto</strong> — no es anónimo como en la carta de regalos.</p>
          <p>Puedes borrar tus propias fotos cuando quieras.</p>
        </HelpButton>
      </div>

      <div style={styles.header}>
        <h2 style={{ color: 'var(--gold)', fontFamily: 'var(--font-display)' }}>📸 Inspiraciones</h2>
        <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>+ Añadir</button>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
        ¿Has visto algo que te gustaría que te regalaran? Súbelo aquí para que la familia lo tenga en cuenta, en cualquier ocasión del año.
      </p>

      {loading ? (
        <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Cargando...</p>
      ) : error ? (
        <p className="error-msg" style={{ textAlign: 'center' }}>⚠ {error}</p>
      ) : inspirations.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
          Todavía no hay ninguna foto de inspiración.
        </p>
      ) : (
        <div style={styles.grid}>
          {inspirations.map(insp => (
            <div key={insp.id} className="card" style={styles.card}>
              <img
                src={insp.photo_url}
                alt=""
                style={styles.photo}
                onClick={() => setLightboxSrc(insp.photo_url)}
              />
              <div style={styles.cardBody}>
                {insp.description && <p style={styles.desc}>{insp.description}</p>}
                <div style={styles.cardFooter}>
                  <span style={styles.author}>{insp.display_name}</span>
                  {insp.user_id === user?.id && (
                    <button className="btn btn-danger" style={styles.deleteBtn} onClick={() => handleDelete(insp.id)}>
                      Borrar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddForm && (
        <AddInspirationModal
          onClose={() => setShowAddForm(false)}
          onAdded={() => { setShowAddForm(false); load(); }}
        />
      )}

      <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </div>
  );
}

function AddInspirationModal({ onClose, onAdded }) {
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  async function handleFileSelected(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setPhotoFile(compressed);
      setPhotoPreview(URL.createObjectURL(compressed));
    } catch (err) {
      setError('No se pudo procesar la foto, inténtalo de nuevo');
    }
  }

  async function handleSave() {
    if (!photoFile) {
      setError('Tienes que elegir o hacer una foto');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.addInspiration(photoFile, description.trim());
      onAdded();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--gold)' }}>Nueva inspiración</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.modalBody}>
          <div>
            {photoPreview ? (
              <div style={styles.previewWrap}>
                <img src={photoPreview} alt="" style={styles.preview} />
                <button className="btn btn-ghost" onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}>
                  Quitar foto
                </button>
              </div>
            ) : (
              <div style={styles.photoButtons}>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => cameraInputRef.current?.click()}>
                  📷 Cámara
                </button>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => fileInputRef.current?.click()}>
                  🖼️ Galería
                </button>
              </div>
            )}
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileSelected} />
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelected} />
          </div>

          <div>
            <label className="label">Comentario (opcional)</label>
            <textarea
              className="input"
              rows={2}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Ej: Me encantó esta lámpara en la tienda de la esquina"
            />
          </div>

          {error && <p className="error-msg">⚠ {error}</p>}
        </div>

        <div style={styles.modalFooter}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Subiendo...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '1rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  card: { padding: 0, overflow: 'hidden' },
  photo: { width: '100%', aspectRatio: '1', objectFit: 'cover', cursor: 'pointer' },
  cardBody: { padding: '0.6rem 0.75rem' },
  desc: { fontSize: '0.82rem', color: 'var(--text)', marginBottom: '0.4rem', lineHeight: 1.3 },
  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  author: { fontSize: '0.75rem', color: 'var(--gold)' },
  deleteBtn: { fontSize: '0.7rem', padding: '0.25rem 0.5rem' },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: '16px 16px 0 0', width: '100%', maxWidth: '500px',
    maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)',
  },
  closeBtn: { background: 'none', color: 'var(--text-muted)', fontSize: '1.2rem', cursor: 'pointer' },
  modalBody: { padding: '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.25rem', flex: 1 },
  modalFooter: {
    padding: '1rem 1.5rem', borderTop: '1px solid var(--border)',
    display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
  },
  photoButtons: { display: 'flex', gap: '0.5rem' },
  previewWrap: { display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' },
  preview: { width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' },
};
